import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarClock, Clock, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { formatSlot } from "@/components/admin/CallSlot";
import { LocalPhotoPicker, uploadApplicationPhotos } from "@/components/apply/TrackFields";

const AddPhotos = ({ onDone }) => {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { onDone(await uploadApplicationPhotos(photos)); toast.success("Photos added to your application."); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 space-y-5 rounded-lg border border-border p-5" data-testid="add-media">
      <div>
        <p className="font-semibold">Add photos <span className="font-normal text-muted-foreground">(optional)</span></p>
        <p className="mt-1 text-sm text-muted-foreground">Up to 6 photos. Only the club's review team can see them.</p>
      </div>
      <LocalPhotoPicker files={photos} onChange={setPhotos} />
      <button type="button" className="btn-primary" disabled={busy || !photos.length} onClick={submit} data-testid="add-media-submit">
        {busy ? "Uploading…" : "Add to application"}
      </button>
    </div>
  );
};

const CallInvite = ({ ci, onDone }) => {
  const [note, setNote] = useState("");
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const respond = async (accept) => {
    setBusy(true);
    try { onDone((await api.post("/application/call/respond", { accept, note })).data); toast.success(accept ? "See you on the call." : "Thanks. The team will suggest another time."); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 space-y-3 rounded-lg border border-primary/40 p-5" data-testid="call-invite">
      <p className="flex items-center gap-2 font-semibold"><CalendarClock className="h-5 w-5 text-primary" /> Your screening video call</p>
      <p className="text-lg" data-testid="call-invite-slot">{formatSlot(ci.slot_at)} · {ci.duration_min} min</p>
      {ci.note && <p className="text-sm text-foreground/85">{ci.note}</p>}
      {ci.status === "accepted" && (
        <>
          <p className="text-sm text-primary" data-testid="call-invite-accepted">You've confirmed this time.</p>
          {ci.link && <a href={ci.link} target="_blank" rel="noreferrer" className="btn-primary" data-testid="call-invite-join">Join call</a>}
        </>
      )}
      {ci.status === "declined" && <p className="text-sm text-muted-foreground" data-testid="call-invite-declined">You asked for another time. The team will send a new one here.</p>}
      {ci.status === "proposed" && (
        <>
          {asking && <textarea rows={2} maxLength={300} className="field py-2" placeholder="Times that suit you better (optional)" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Times that suit you better" data-testid="call-invite-note" />}
          <div className="flex flex-wrap gap-3">
            {!asking && <button type="button" className="btn-primary" disabled={busy} onClick={() => respond(true)} data-testid="call-invite-accept">Accept this time</button>}
            {asking
              ? <button type="button" className="btn-primary" disabled={busy} onClick={() => respond(false)} data-testid="call-invite-decline-submit">Send request</button>
              : <button type="button" className="btn-outline" disabled={busy} onClick={() => setAsking(true)} data-testid="call-invite-decline">Ask for another time</button>}
          </div>
        </>
      )}
    </div>
  );
};

const BookingCard = ({ user }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (user.status !== "pending") return;
    api.get("/application/booking").then(({ data }) => setUrl(data.url)).catch(() => setUrl(null));
  }, [user]);
  if (!url) return null;
  return (
    <div className="mt-6 space-y-3 rounded-lg border border-primary/40 p-5" data-testid="booking-card">
      <p className="flex items-center gap-2 font-semibold"><CalendarClock className="h-5 w-5 text-primary" /> You're invited to a screening video call</p>
      <p className="text-sm text-muted-foreground">Pick a time that suits you. You'll get a confirmation with the call details once you book.</p>
      <a href={url} target="_blank" rel="noreferrer" className="btn-primary" data-testid="booking-card-button">Book your screening call</a>
    </div>
  );
};

const STATES = {
  pending: { icon: Clock, title: "Application received", body: "Thank you. Your application is being privately screened. Access to the members' area is granted once your membership is approved.", tone: "text-[hsl(var(--gold))]" },
  approved: { icon: CheckCircle2, title: "Welcome to the club", body: "Your membership has been approved. Your circle is waiting.", tone: "text-primary" },
  suspended: { icon: XCircle, title: "Membership suspended", body: "Your membership has been suspended while our team reviews your account. Please reply to our email if you think this was a mistake.", tone: "text-destructive" },
  rejected: { icon: XCircle, title: "Application not approved", body: "Thank you for your interest. Your application was not approved at this time.", tone: "text-destructive" },
};

export default function Status() {
  const { user, refresh, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const s = STATES[user.status] || STATES.pending;

  const recheck = async () => {
    setChecking(true);
    await refresh();
    setChecking(false);
  };

  return (
    <AuthLayout testId="status-page">
      <p className="eyebrow mb-6">Membership status</p>
      <div className="surface p-7 sm:p-9" data-testid={`status-${user.status}`}>
        <s.icon className={`h-9 w-9 ${s.tone}`} strokeWidth={1.5} />
        <h1 className="font-display mt-5 text-4xl sm:text-5xl">{s.title}</h1>
        <p className="mt-4 text-foreground/85">{s.body}</p>
        {user.status === "rejected" && user.review_note && <p className="mt-4 rounded-lg bg-secondary p-4 text-sm" data-testid="status-review-note">{user.review_note}</p>}
        <BookingCard user={user} />
        {user.status === "pending" && user.call_invite && <CallInvite ci={user.call_invite} onDone={setUser} />}
        {user.status === "pending" && user.application_track && !user.application_has_media && <AddPhotos onDone={setUser} />}
        <dl className="mt-6 grid gap-1 text-sm text-muted-foreground">
          <div><dt className="inline">Applicant: </dt><dd className="inline text-foreground">{user.name}</dd></div>
          <div><dt className="inline">Email: </dt><dd className="inline text-foreground">{user.email}</dd></div>
        </dl>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          {user.status === "approved" ? (
            <Link to="/connect" className="btn-primary" data-testid="status-enter-button">Enter the members' area</Link>
          ) : (
            <button type="button" className="btn-primary" onClick={recheck} disabled={checking} data-testid="status-refresh-button">
              <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} /> Check status
            </button>
          )}
          <button type="button" className="btn-outline" onClick={async () => { await logout(); navigate("/"); }} data-testid="status-signout-button">Sign out</button>
        </div>
      </div>
    </AuthLayout>
  );
}
